paf-text-input {
  @include host-component-properties-without-host;

  --paf-input-background-color: var(--paf-global-post-brand-white);
  --paf-input-font-size: 16;
  --paf-input-line-height: 16;
  --paf-input-padding-block: 25;
  --paf-input-floated-padding-top: 35;
  --paf-input-floated-padding-bottom: 15;
  --paf-input-padding-inline: 16;
  --paf-input-placeholder-color: var(--paf-global-post-brand-secondary);
  --paf-input-text-color: var(--paf-global-post-brand-black);
  --paf-input-border-color: var(--paf-global-post-brand-secondary);
  --paf-input-border-color-active: var(--paf-global-post-brand-black);
  --paf-input-border-width: 2;
  --paf-input-focus-border-radius: var(--paf-global-spacing-fixed-1x);
  --paf-input-floated-label-top: 14;
  --paf-input-floated-label-transform: 75%;
  --paf-input-label-color: #43454e;
  --paf-input-min-height: 72;
  --paf-input-transition-duration: 250ms;
  --paf-input-error-accent-color: var(--paf-global-post-error);
  --paf-input-error-message-color: var(--paf-global-post-brand-white);
  --paf-input-error-message-size: 12;
  --paf-input-error-message-padding-block: 6;
  --paf-input-error-message-padding-inline: 16;
  --paf-input-icon-dimensions: 32;
  --paf-input-icon-margin-inline: 12;

  &:has(:focus-visible) {
    box-shadow: 0 0 0.25rem 0 var(--paf-input-border-color-active);
    border-radius: px-to-rem(var(--paf-input-focus-border-radius));
  }

  &:has([aria-invalid]:focus-visible) {
    @include focus-visible;

    box-shadow: 0 0 0.25rem 0 var(--paf-input-error-accent-color);
    outline-color: var(--paf-input-error-accent-color);
  }
}

.paf-text-input {
  position: relative;
}

.paf-text-input__input {
  appearance: none;
  text-align: start;
  text-indent: 0;
  font-size: px-to-rem(var(--paf-input-font-size));
  line-height: px-to-rem(var(--paf-input-line-height));
  width: 100%;
  min-height: px-to-rem(var(--paf-input-min-height));
  padding: px-to-rem(var(--paf-input-padding-block)) px-to-rem(var(--paf-input-padding-inline));
  color: var(--paf-input-text-color);
  border: px-to-rem(var(--paf-input-border-width)) solid var(--paf-input-border-color);
  background-color: transparent;
  -webkit-tap-highlight-color: transparent;
  border-radius: 0;
  transition: padding var(--paf-input-transition-duration), border-color var(--paf-input-transition-duration);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  .paf-text-input--has-error-icon & {
    $totalMargin: calc(2 * px-to-rem(var(--paf-input-icon-margin-inline)));

    padding-inline-end: calc(px-to-rem(var(--paf-input-icon-dimensions)) + $totalMargin);
  }

  @include reduced-motion {
    transition: none;
  }

  &::placeholder {
    color: transparent;
    transition: color var(--paf-input-transition-duration);

    @include reduced-motion {
      transition: none;
    }

    @include forced-colors {
      opacity: 0;
      transition: opacity var(--paf-input-transition-duration);
    }
  }

  &:focus,
  &:active {
    border-color: var(--paf-input-border-color-active);

    &::placeholder {
      color: var(--paf-input-placeholder-color);

      @include forced-colors {
        opacity: 1;
      }

      .paf-text-input--no-placeholder & {
        color: transparent;

        @include forced-colors {
          opacity: 0;
        }
      }
    }
  }

  &:focus,
  &:active,
  &:not(:placeholder-shown) {
    padding-block: px-to-rem(var(--paf-input-floated-padding-top)) px-to-rem(var(--paf-input-floated-padding-bottom));
  }
}

.paf-text-input__label {
  position: absolute;
  inset-block-start: calc(50% - px-to-rem(var(--paf-input-line-height) / 2));
  inset-inline-start: px-to-rem(var(--paf-input-padding-inline));
  inset-inline-end: px-to-rem(calc(var(--paf-input-padding-inline) + var(--paf-input-icon-dimensions)));
  color: var(--paf-input-label-color);
  pointer-events: none;
  transform-origin: left center;
  transition: transform var(--paf-input-transition-duration), top var(--paf-input-transition-duration);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  @include reduced-motion {
    transition: none;
  }
}

.paf-text-input__input:focus + .paf-text-input__label,
.paf-text-input__input:not(:placeholder-shown) + .paf-text-input__label {
  inset-block-start: px-to-rem(var(--paf-input-floated-label-top));
  transform: scale(var(--paf-input-floated-label-transform));
}

.paf-text-input__error-message {
  display: none;
  background-color: var(--paf-input-error-accent-color);
  color: var(--paf-input-error-message-color);
  font-size: px-to-rem(var(--paf-input-error-message-size));
  padding-block: px-to-rem(var(--paf-input-error-message-padding-block));
  padding-inline: px-to-rem(var(--paf-input-error-message-padding-inline));
  border-end-start-radius: px-to-rem(var(--paf-input-focus-border-radius));
  border-end-end-radius: px-to-rem(var(--paf-input-focus-border-radius));

  @include forced-colors {
    background-color: LinkText;
  }
}

.paf-text-input__error-icon {
  display: none;
  position: absolute;
  z-index: 1;
  pointer-events: none;
  width: px-to-rem(var(--paf-input-icon-dimensions));
  height: px-to-rem(var(--paf-input-icon-dimensions));
  inset-block-start: calc(50% - px-to-rem(var(--paf-input-icon-dimensions) / 2));
  inset-inline-end: px-to-rem(var(--paf-input-icon-margin-inline));
  fill: var(--paf-input-error-accent-color);

  @include forced-colors {
    fill: LinkText;
  }

  .paf-text-input--has-icon & {
    $margin-inline: calc(2 * px-to-rem(var(--paf-input-icon-margin-inline)));
    $inline-end: calc($margin-inline + px-to-rem(var(--paf-input-icon-dimensions)));

    inset-inline-end: $inline-end;
  }
}

.paf-text-input.paf-text-input--has-error {
  & .paf-text-input__input {
    border-color: var(--paf-input-error-accent-color);
    border-end-start-radius: 0;
    border-end-end-radius: 0;
    outline: none;
  }

  & .paf-text-input__error-icon,
  & ~ .paf-text-input__error-message {
    display: block;
  }

  & ~ .paf-text-input__error-message {
    position: relative;
    z-index: 2;
  }
}

// Hide calendar and date icons for most browsers
input[type="date"]::-webkit-inner-spin-button,
input[type="date"]::-webkit-calendar-picker-indicator,
input[type="time"]::-webkit-inner-spin-button,
input[type="time"]::-webkit-calendar-picker-indicator {
  display: none;
  appearance: none;
}

input::-webkit-date-and-time-value {
  text-align: left;
}

// Hide calendar and date icons for firefox
.paf-text-input--has-icon:after {
  background: red;
  position: absolute;
  content: "";
  background-color: var(--paf-input-background-color);
  top: px-to-rem(var(--paf-input-border-width));
  right: px-to-rem(var(--paf-input-border-width));
  bottom: px-to-rem(var(--paf-input-border-width));
  width: calc(px-to-rem(var(--paf-input-icon-dimensions)) + 2 * px-to-rem(var(--paf-input-icon-margin-inline)));
  z-index: 0;
}

.paf-text-input__supplementary-button {
  @include button-reset;

  z-index: 1;
  cursor: pointer;
  position: absolute;
  inset-block-start: calc(50% - px-to-rem(var(--paf-input-icon-dimensions)) / 2);
  inset-inline-end: px-to-rem(var(--paf-input-icon-margin-inline));
  width: px-to-rem(var(--paf-input-icon-dimensions));
  height: px-to-rem(var(--paf-input-icon-dimensions));

  .paf-text-input__supplementary-icon {
    fill: var(--paf-input-border-color);
    width: 100%;
    height: 100%;

    @include forced-colors {
      fill: CanvasText;

      &:focus,
      &:hover {
        fill: ButtonText;
      }
    }
  }
}
